import { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { getSSRWidth } from '@utils/device';
// ЭТАП 1 · Шаг 3 — стартовая ширина ИЗОМОРФНО: сервер — по User-Agent
// (getSSRWidth), первый клиентский рендер — по проброшенному window.__SSR_DEVICE__
// (совпадает с сервером → нет hydration-mismatch), реальный размер — useEffect
// ниже сразу после mount. НЕ на module-eval: getSSRWidth зовём per-render.

interface useWindowSizeReturnType {
  windowWidth: number;
  viewportHeight: number;
  isDesktop: boolean;
  isSmallDesktop: boolean;
  isTablet: boolean;
  isMobile: boolean;
}

const useWindowSize = (wait: number = 0): useWindowSizeReturnType => {
  const [windowParams, setWindowParams] = useState(() => ({
    windowWidth: getSSRWidth(),
    viewportHeight: 0
  }));

  const onWindowResize = useCallback((): void => {
    setWindowParams({
      viewportHeight: window.innerHeight,
      windowWidth:
        document.documentElement.clientWidth || window.innerWidth || 0
    });
  }, []);

  const debouncedOnWindowResize = useMemo(
    () => {
      if (wait === 0) {
        return onWindowResize;
      }
      return debounce(onWindowResize, wait);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onWindowResize]
  );

  useEffect(() => {
    debouncedOnWindowResize();
    window.addEventListener('resize', debouncedOnWindowResize, {
      passive: true
    });
    return () => {
      window.removeEventListener('resize', debouncedOnWindowResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const result = useMemo(
    () => ({
      windowWidth: windowParams.windowWidth,
      viewportHeight: windowParams.viewportHeight,
      isDesktop: windowParams.windowWidth >= 1280,
      isSmallDesktop:
        windowParams.windowWidth >= 1280 && windowParams.windowWidth <= 1439,
      isTablet:
        windowParams.windowWidth >= 768 && windowParams.windowWidth < 1280,
      isMobile: windowParams.windowWidth < 768
    }),
    [windowParams.windowWidth, windowParams.viewportHeight]
  );

  return result;
};

export default useWindowSize;
